import React, { Fragment } from 'react';
import Store from 'state/Store';

import BaseCard from 'components/shared/Card/BaseCard';
import SpeedometerChart from 'components/shared/SpeedometerChart/SpeedometerChart';
import {
  yearOptions,
  getChartTextProperties,
  getBodyText,
  getPercentChange
} from 'services/IRAContributionCard';
import MobileToggleBar from 'components/shared/ToggleBar/MobileToggleBar';
import { changeContributionYear } from 'state/Actions';
import { CURRENT_YEAR } from 'services/Dates';
import { vanityBuy } from 'services/Externals';
import { ReactComponent as IRAContributionsComplete } from 'assets/ira-contributions-complete.svg';

const IRAContributionCard = ({ data, isLoading, hasError }) => {
  const {
    iraContributionPeriod: { isWithinPriorYearContributionPeriod },
    iraContributionLimit,
    iraContributionYearToggle
  } = data;

  const {
    currentYearContributionsSoFar,
    priorYearContributionsSoFar,
    currentYearRemainingAutomaticPurchases,
    priorYearRemainingAutomaticPurchases,
    currentYearMaximumLimit,
    priorYearMaximumLimit
  } = iraContributionLimit;

  const handleYearChange = (value) => {
    Store.dispatch(changeContributionYear(value));
  };

  const toggleValue = !isWithinPriorYearContributionPeriod
    ? CURRENT_YEAR.toString()
    : iraContributionYearToggle;

  const currentYearTotalContributions =
    currentYearContributionsSoFar + currentYearRemainingAutomaticPurchases;
  const priorYearTotalContributions =
    priorYearContributionsSoFar + priorYearRemainingAutomaticPurchases;

  const hasCurrentYearRemainingAutomaticPurchases =
    currentYearRemainingAutomaticPurchases > 0;

  const percentChange =
    toggleValue === CURRENT_YEAR.toString()
      ? getPercentChange(currentYearTotalContributions, currentYearMaximumLimit)
      : getPercentChange(priorYearTotalContributions, priorYearMaximumLimit);

  const chartTextProperties = getChartTextProperties(percentChange);
  const bodyText =
    toggleValue === CURRENT_YEAR.toString()
      ? getBodyText(
          currentYearTotalContributions,
          hasCurrentYearRemainingAutomaticPurchases,
          currentYearMaximumLimit,
          percentChange
        )
      : getBodyText(
          priorYearTotalContributions,
          false,
          priorYearMaximumLimit,
          percentChange
        );

  const showIncludesScheduledContributions =
    percentChange !== 100 && percentChange !== 0;
  const toggle = isWithinPriorYearContributionPeriod ? (
    <MobileToggleBar
      onClick={handleYearChange}
      options={yearOptions}
      selection={toggleValue}
    />
  ) : null;

  const footer =
    percentChange === 100 ? null : (
      <a
        href={vanityBuy('Contribute')}
        className="card-action-link"
        rel="noopener noreferrer"
      >
        Contribute Now
      </a>
    );

  if (hasError) {
    return null;
  }

  return (
    <BaseCard
      isLoading={isLoading}
      title="IRA Contributions"
      toggle={toggle}
      footer={footer}
      className="standard ira-contribution-card-container"
    >
      {percentChange === 100 ? (
        <Fragment>
          <h2 className="ira-contribution-completed-heading">
            {toggleValue} IRA Contributions
          </h2>
          <IRAContributionsComplete />
          <div className="ira-contribution-completed-text">Great Job!</div>
        </Fragment>
      ) : (
        <SpeedometerChart
          percentChange={percentChange}
          textX={chartTextProperties.textX}
          text={chartTextProperties.text}
        />
      )}

      <p className="ira-contribution-card-sub-text">{bodyText}</p>

      {showIncludesScheduledContributions && (
        <p className="ira-contribution-card-includes-scheduled">
          *Includes scheduled contributions
        </p>
      )}
    </BaseCard>
  );
};

export default IRAContributionCard;
